import { cn } from '@/utils/cn'
import { Button } from '../common/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../common/table'
import { LoadingIcon } from '../icons/loading'

interface BridgeOption {
  type: 'fast' | 'standard'
  estimateSpeed: string
  confirmation: string
  feeAmount?: string
  receiveAmount?: string
}

interface BridgeOptionsProps {
  fastBridge: BridgeOption
  standardBridge: BridgeOption
  onSelectBridge: (type: 'fast' | 'standard') => void
  loading?: boolean
  disabled?: boolean
  bridging?: boolean
  className?: string
}

interface BridgeOptionCardProps {
  option: BridgeOption
  onSelect: () => void
  loading?: boolean
  disabled?: boolean
  bridging?: boolean
  variant: 'primary' | 'secondary'
}

const BridgeOptionCard = ({ option, onSelect, loading, disabled, bridging, variant }: BridgeOptionCardProps) => {
  const isPrimary = variant === 'primary'
  const title = option.type === 'fast' ? 'Fast Bridge' : 'Standard Bridge'
  const buttonText = bridging ? 'Bridge in progress' : option.type === 'fast' ? 'Fast bridge' : 'Standard bridge'

  return (
    <div className={cn('rounded-2xl p-4', isPrimary ? 'bg-surface' : 'bg-page/50')}>
      <h3 className="mb-3 font-semibold text-base text-heading-foreground">{title}</h3>
      <div className="flex flex-col gap-2 text-sm">
        <div className="flex justify-between">
          <span>Estimated speed</span>
          <span className="text-heading-foreground">{option.estimateSpeed}</span>
        </div>
        <div className="flex justify-between">
          <span>Confirmation</span>
          <span className="text-heading-foreground">{option.confirmation}</span>
        </div>
        <div className="flex justify-between">
          <span>Fee amount</span>
          <span className="text-heading-foreground">
            {option.feeAmount ? `${option.feeAmount} USDC` : loading ? 'Estimating...' : '–'}
          </span>
        </div>
        <div className="flex justify-between">
          <span>You will receive</span>
          <span className="text-heading-foreground">
            {option.receiveAmount ? `${option.receiveAmount} USDC` : loading ? 'Estimating...' : '–'}
          </span>
        </div>
      </div>
      <div className="mt-4">
        <Button
          variant={isPrimary ? 'primary' : 'secondary'}
          size="md"
          onClick={onSelect}
          disabled={disabled}
          className="flex w-full items-center justify-center gap-2"
        >
          {bridging && <LoadingIcon size={16} className="animate-spin" />}
          <span>{buttonText}</span>
        </Button>
      </div>
    </div>
  )
}

const BridgeOptionsCards = ({
  fastBridge,
  standardBridge,
  onSelectBridge,
  loading,
  disabled,
  bridging,
  className,
}: BridgeOptionsProps) => {
  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <BridgeOptionCard
        option={fastBridge}
        onSelect={() => onSelectBridge('fast')}
        loading={loading}
        disabled={disabled}
        bridging={bridging}
        variant="primary"
      />
      <BridgeOptionCard
        option={standardBridge}
        onSelect={() => onSelectBridge('standard')}
        loading={loading}
        disabled={disabled}
        bridging={bridging}
        variant="secondary"
      />
    </div>
  )
}

const BridgeOptionsTable = ({
  fastBridge,
  standardBridge,
  onSelectBridge,
  loading,
  disabled,
  bridging,
  className,
}: BridgeOptionsProps) => {
  return (
    <Table className={cn('overflow-auto', className)}>
      <TableHeader>
        <TableRow className="border-b">
          <TableHead className="w-1/4 py-1 pr-3" />
          <TableHead className="w-1/3 px-3 py-1 text-center">
            <span className="font-semibold text-base text-heading-foreground">Fast Bridge</span>
          </TableHead>
          <TableHead className="w-1/3 px-3 py-1 text-center">
            <span className="font-semibold text-base text-heading-foreground">Standard Bridge</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody className="[&>tr]:border-b">
        <TableRow variant="default">
          <TableCell className="py-1 pr-3 text-sm">Estimated speed</TableCell>
          <TableCell className="px-3 py-1 text-center text-heading-foreground text-sm">
            {fastBridge.estimateSpeed}
          </TableCell>
          <TableCell className="px-3 py-1 text-center text-heading-foreground text-sm">
            {standardBridge.estimateSpeed}
          </TableCell>
        </TableRow>
        <TableRow variant="default">
          <TableCell className="py-1 pr-3 text-sm">Confirmation</TableCell>
          <TableCell className="px-3 py-1 text-center text-heading-foreground text-sm">
            {fastBridge.confirmation}
          </TableCell>
          <TableCell className="px-3 py-1 text-center text-heading-foreground text-sm">
            {standardBridge.confirmation}
          </TableCell>
        </TableRow>
        <TableRow variant="default">
          <TableCell className="py-1 pr-3 text-sm">Fee amount</TableCell>
          <TableCell className="px-3 py-1 text-center text-heading-foreground text-sm">
            {fastBridge.feeAmount ? `${fastBridge.feeAmount} USDC` : loading ? 'Estimating...' : '–'}
          </TableCell>
          <TableCell className="px-3 py-1 text-center text-heading-foreground text-sm">
            {standardBridge.feeAmount ? `${standardBridge.feeAmount} USDC` : loading ? 'Estimating...' : '–'}
          </TableCell>
        </TableRow>
        <TableRow variant="default" className="border-transparent">
          <TableCell className="py-1 pr-3 text-sm">You will receive</TableCell>
          <TableCell className="px-3 py-1 text-center text-heading-foreground text-sm">
            {fastBridge.receiveAmount ? `${fastBridge.receiveAmount} USDC` : loading ? 'Estimating...' : '–'}
          </TableCell>
          <TableCell className="px-3 py-1 text-center text-heading-foreground text-sm">
            {standardBridge.receiveAmount ? `${standardBridge.receiveAmount} USDC` : loading ? 'Estimating...' : '–'}
          </TableCell>
        </TableRow>
        <TableRow variant="default" className="border-transparent">
          <TableCell className="pt-2 pr-3" />
          <TableCell className="px-3 pt-2">
            <Button
              variant="primary"
              size="md"
              onClick={() => onSelectBridge('fast')}
              disabled={disabled}
              className="flex w-full items-center justify-center gap-2"
            >
              {bridging && <LoadingIcon size={16} className="animate-spin" />}
              <span>{bridging ? 'Bridge in progress' : 'Fast bridge'}</span>
            </Button>
          </TableCell>
          <TableCell className="px-3 pt-2">
            <Button
              variant="secondary"
              size="md"
              onClick={() => onSelectBridge('standard')}
              disabled={disabled}
              className="flex w-full items-center justify-center gap-2"
            >
              {bridging && <LoadingIcon size={16} className="animate-spin" />}
              <span>{bridging ? 'Bridge in progress' : 'Standard bridge'}</span>
            </Button>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  )
}

export const BridgeOptions = (props: BridgeOptionsProps) => {
  return (
    <>
      <BridgeOptionsCards {...props} className="sm:hidden" />
      <BridgeOptionsTable {...props} className="hidden sm:table" />
    </>
  )
}
